import { ApiRequestMeta, useRestApiProvider } from "@jugl-web/rest-api";
import {
  CRM_GMAIL_OAUTH_PENDING_REDIRECT_KEY,
  CRM_GMAIL_OAUTH_RESULT_PENDING_KEY,
  getStorageItem,
  saveItemToStorage,
} from "@jugl-web/utils/storage";
import { useToast, useTranslations } from "@jugl-web/utils";
import React, { useCallback, useEffect, useMemo } from "react";
import { environment } from "@web-src/environments/environment";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useManageAccountsDrawer } from "../../components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { EmailAccountContext } from "./EmailAccountContext";
import { extractNonEmptyAccount } from "../../utils/extractNonEmptyAccount";

// must outlast the OAuth round-trip but ignore stale markers left behind by an
// abandoned attempt
const GMAIL_OAUTH_RESULT_TTL_MS = 10 * 60 * 1000;

export const EmailAccountProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { integrationsApi } = useRestApiProvider();
  const { crmConfig, isCRMConfigLoading } = useManageAccountsDrawer();
  const { entityId } = useEntitySelectedProvider();
  const { toast } = useToast();
  const { t } = useTranslations();

  const [disconnectEmailAccountMutation, { isLoading: isDisconnecting }] =
    integrationsApi.useDisconnectEmailAccountMutation();

  const emailAccount = useMemo(
    () => extractNonEmptyAccount(crmConfig?.email),
    [crmConfig?.email]
  );

  const isConnected = !!emailAccount?.id;

  // Connecting Gmail is a full-page OAuth redirect, so the success toast is
  // fired here once we're back in the app and the email config has loaded.
  // NavigateToDefaultEntity leaves CRM_GMAIL_OAUTH_RESULT_PENDING_KEY on return.
  useEffect(() => {
    if (isCRMConfigLoading) {
      return;
    }

    const pendingResult = getStorageItem<{
      entityId: string;
      createdAt: number;
      authSuccess?: string | null;
      result?: string | null;
    }>(CRM_GMAIL_OAUTH_RESULT_PENDING_KEY);

    if (!pendingResult || pendingResult.entityId !== entityId) {
      return;
    }

    localStorage.removeItem(CRM_GMAIL_OAUTH_RESULT_PENDING_KEY);

    const isFresh =
      Date.now() - pendingResult.createdAt < GMAIL_OAUTH_RESULT_TTL_MS;

    if (!isFresh) {
      return;
    }

    const genericError = () =>
      toast(
        t({
          id: "client-support-page.gmail-account-connect-failed",
          defaultMessage: "Couldn't connect Gmail account",
        }),
        { variant: "error" }
      );
    // Known backend failures are resolved from the result param (the account
    // won't be connected in these cases, so we can't rely on isConnected)
    if (pendingResult.result === "account_already_exists") {
      toast(
        t({
          id: "client-support-page.gmail-account-already-exists",
          defaultMessage:
            "Google Account is already connected to another Workspace. Please disconnect it first and try again",
        }),
        { variant: "error" }
      );
    } else if (pendingResult.result === "denied") {
      toast(
        t({
          id: "client-support-page.gmail-account-access-denied",
          defaultMessage:
            "Access is denied. Please provide access to your Google Account to connect to Gmail Inbox",
        }),
        { variant: "error" }
      );
    } else if (pendingResult.authSuccess === "false") {
      genericError();
    } else if (isConnected) {
      toast(
        t({
          id: "client-support-page.gmail-account-connected",
          defaultMessage: "Gmail account connected successfully",
        })
      );
    } else {
      genericError();
    }
  }, [isCRMConfigLoading, isConnected, entityId, t, toast]);

  const connectGmailAccount = useCallback(() => {
    // the OAuth backend only accepts a bare origin as final_callback_url, so
    // remember where to land after the round-trip (consumed on app boot in
    // NavigateToDefaultEntity)
    saveItemToStorage(CRM_GMAIL_OAUTH_PENDING_REDIRECT_KEY, {
      entityId,
      createdAt: Date.now(),
    });

    window.location.href = `${
      environment.apiUrl
    }/jugl/uberauth/gmail?final_callback_url=${encodeURIComponent(
      window.location.origin
    )}&agt=web&entity_id=${entityId}`;
  }, [entityId]);

  const disconnectEmailAccount = useCallback(
    async (meta?: ApiRequestMeta) => {
      await disconnectEmailAccountMutation({ entityId, meta }).unwrap();
    },
    [disconnectEmailAccountMutation, entityId]
  );

  const contextValue = useMemo(
    () => ({
      emailAccount,
      isConnected,
      connectGmailAccount,
      disconnectEmailAccount,
      isDisconnectingEmailAccount: isDisconnecting,
    }),
    [
      emailAccount,
      isConnected,
      connectGmailAccount,
      disconnectEmailAccount,
      isDisconnecting,
    ]
  );

  return (
    <EmailAccountContext.Provider value={contextValue}>
      {children}
    </EmailAccountContext.Provider>
  );
};
